import React from 'react';
import { motion } from 'framer-motion';

const ScorePip = ({ filled }) => (
  <div className={`w-3 h-3 rounded-full transition-all ${filled ? 'bg-current' : 'bg-current opacity-20'}`} />
);

const ScoreBar = ({ score, max = 10, light = false }) => (
  <div className="flex items-center gap-1.5 mt-3">
    {Array.from({ length: max }).map((_, i) => (
      <ScorePip key={i} filled={i < score} />
    ))}
    <span className={`ml-2 text-sm font-semibold ${light ? 'text-blue-200' : 'text-jeruel-primary'}`}>
      {score}/10 Exposure
    </span>
  </div>
);

const Comparison = () => {
  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-jeruel-offwhite">
      <div className="max-w-6xl mx-auto">
        
        <div className="text-center mb-16">
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm font-bold text-red-500 uppercase tracking-widest mb-3"
          >
            A Glimpse Into The Data
          </motion.p>
          <motion.h2 
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-jeruel-dark"
          >
            Two workforces. <br className="hidden sm:block" />
            Two very different futures.
          </motion.h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-16">
          
          {/* Physical Workforce Card */}
          <motion.div 
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="relative bg-white rounded-2xl border border-jeruel-gray shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col"
          >
            {/* Photographic Header */}
            <div className="relative h-48 w-full overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&q=80&w=800" 
                alt="Industrial Maintenance Worker" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 backdrop-blur-md border border-emerald-400/30 text-white text-xs font-bold uppercase tracking-wider mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Protected Roles
                </div>
                <h3 className="text-2xl font-bold text-white">The Physical Workforce</h3>
              </div>
            </div>
            
            <div className="p-8 flex-1 flex flex-col">
              <div className="text-emerald-600 mb-1">
                <ScoreBar score={2} />
              </div>
              <p className="text-xs text-gray-500 font-bold mb-6">SAFE FROM AUTOMATION</p>
              
              <div className="h-px bg-jeruel-gray mb-6" />
              
              <p className="text-gray-900 font-bold mb-3 text-lg">
                Technicians, electricians, millwrights, welders.
              </p>
              <p className="text-gray-600 leading-relaxed font-medium flex-1">
                These roles face a supply crisis rather than a demand crisis. The BLS projects massive shortages, meaning you need to hire and retain them at all costs. After all, AI cannot turn a wrench.
              </p>

              <div className="mt-8 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <p className="text-lg font-bold text-emerald-900">54,200 openings/year</p>
                <p className="text-sm text-emerald-700 mt-0.5 font-medium">Projected shortage for industrial mechanics</p>
              </div>
            </div>
          </motion.div>

          {/* Knowledge Workforce Card */}
          <motion.div 
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="relative bg-jeruel-primary rounded-2xl shadow-xl overflow-hidden flex flex-col border border-jeruel-primary"
          >
            {/* Photographic Header */}
            <div className="relative h-48 w-full overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1581092335397-9583eb92d232?auto=format&fit=crop&q=80&w=800" 
                alt="Knowledge Worker in Control Room" 
                className="w-full h-full object-cover mix-blend-luminosity"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-jeruel-primary to-jeruel-primary/40" />
              <div className="absolute bottom-4 left-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/30 backdrop-blur-md border border-red-400/30 text-white text-xs font-bold uppercase tracking-wider mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                  Exposed Roles
                </div>
                <h3 className="text-2xl font-bold text-white">The Knowledge Workforce</h3>
              </div>
            </div>
            
            <div className="p-8 flex-1 flex flex-col relative z-10">
              <div className="text-white">
                <ScoreBar score={7} light />
              </div>
              <p className="text-xs text-red-300 font-bold mb-6">HIGHLY EXPOSED TO AI</p>
              
              <div className="h-px bg-white/20 mb-6" />
              
              <p className="text-white font-bold mb-3 text-lg">
                Planners, reliability engineers, PdM analysts, CMMS admins.
              </p>
              <p className="text-blue-100 leading-relaxed font-medium flex-1">
                One AI-augmented planner will soon be able to cover the work of three people. If you are still hiring for these roles using 2019 ratios, you might be overspending by hundreds of thousands of dollars.
              </p>

              <div className="mt-8 p-4 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm">
                <p className="text-lg font-bold text-white">1:20 ratio shifting to 1:40</p>
                <p className="text-sm text-blue-200 mt-0.5 font-medium">New AI-enabled planner-to-technician coverage</p>
              </div>
            </div>
          </motion.div>

        </div>

        {/* Mid-page CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto"
        >
          <p className="text-xl font-bold text-jeruel-dark mb-6">
            These are just two of the 19 roles we analyzed. You can't afford to guess where the other 17 land on the exposure scale.
          </p>
          <a 
            href="#download-form"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-jeruel-dark hover:bg-black text-white font-bold text-base transition-all duration-300 shadow-xl hover:-translate-y-1"
          >
            Get the Complete Role Breakdown Now
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </a>
        </motion.div>

      </div>
    </section>
  );
};

export default Comparison;